import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type Proposal = { title: string; angle: string; why: string; signal: 'question' | 'plays' | 'comments' | 'gap'; question: string | null };
const SIGNAL_LABEL = { question: 'viewers asked', plays: 'most played', comments: 'the comments ask', gap: 'a gap in the run' } as const;

// The director plans the week: three episodes from what the door reported
// back. Each is a separate yes or no — a yes makes the card with the angle as
// its brief; a no stays on screen, greyed, the way the director's other
// proposals do, so the record of what was decided is kept.
export function PlanWeek({ brandId, onMade }: { brandId: string | null; onMade: (cardId: string) => void | Promise<void> }) {
  const [plan, setPlan] = useState<Proposal[] | null>(null);
  const [signals, setSignals] = useState<{ questions: number; topPlayed: number; comments: number; recent: number } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decided, setDecided] = useState<Record<string, 'made' | 'no'>>({});
  const [plannedAt, setPlannedAt] = useState<string | null>(null);
  // 148: the plan as kept on the workspace, while it is less than a week old.
  useEffect(() => {
    api.weekPlan().then((r) => {
      if (r.plannedAt && Date.now() - new Date(r.plannedAt).getTime() < 7 * 86_400_000 && r.episodes.length) { setPlan(r.episodes); setPlannedAt(r.plannedAt); }
    }).catch(() => undefined);
  }, []);
  const ask = () => {
    setBusy('plan'); setError(null);
    api.planWeek().then((r) => { setPlan(r.episodes); setSignals(r.signals); setPlannedAt(r.plannedAt); setDecided({}); }).catch((e: Error) => setError(e.message)).finally(() => setBusy(null));
  };
  const make = async (p: Proposal) => {
    setBusy(p.title); setError(null);
    try {
      const card = await api.createCard({ title: p.title, brandId, topic: p.angle.slice(0, 200), painPoint: p.question ? `Viewers asked: "${p.question}"` : undefined });
      await api.saveBrief(card.id, [p.angle, '', `Why now: ${p.why}`].join('\n')).catch(() => undefined);
      setDecided((d) => ({ ...d, [p.title]: 'made' }));
      await onMade(card.id);
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  return (
    <div data-tool="plan-week" style={{ margin: '4px 0 8px' }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <button type="button" className="btn tiny" data-move="plan" disabled={busy === 'plan'} onClick={ask} title="Three episodes from what the door reported back: unanswered questions, what was played most, what the comments ask for">{busy === 'plan' ? 'Planning…' : plan ? 'Plan the week again' : 'Plan the week'}</button>
        {signals && <span className="sub-dim">from {signals.questions} open question{signals.questions === 1 ? '' : 's'} · {signals.topPlayed} most played · {signals.comments} comment{signals.comments === 1 ? '' : 's'} · {signals.recent} recent episode{signals.recent === 1 ? '' : 's'}</span>}
        {!signals && plannedAt && <span className="sub-dim">planned {new Date(plannedAt).toLocaleDateString()} · kept for the team</span>}
      </div>
      {error && <div className="err inline">{error}</div>}
      {plan && plan.length === 0 && <p className="sub-dim" style={{ margin: '6px 0 0' }}>The director has nothing to add yet — publish an episode and let the door report back.</p>}
      {plan && plan.length > 0 && (
        <div className="row wrap" style={{ gap: 8, marginTop: 6, alignItems: 'stretch' }}>
          {plan.map((p) => {
            const d = decided[p.title];
            return (
              <div key={p.title} style={{ flex: '1 1 260px', border: '1px solid var(--line)', borderRadius: 10, padding: 10, opacity: d === 'no' ? 0.5 : 1 }} data-tool="plan-proposal">
                <div className="sub-dim"><span className="chip">{SIGNAL_LABEL[p.signal]}</span></div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>{p.title}</div>
                <p style={{ fontSize: 13, margin: '4px 0 0' }}>{p.angle}</p>
                <p className="sub-dim" style={{ margin: '4px 0 0' }}>{p.why}</p>
                <div className="row" style={{ gap: 6, marginTop: 8 }}>
                  {d === 'made' ? <span className="chip">on the board</span> : d === 'no' ? <span className="chip">not this</span> : (
                    <>
                      <button type="button" className="btn tiny pri" disabled={busy === p.title} onClick={() => void make(p)}>{busy === p.title ? 'Making…' : 'Make this card'}</button>
                      <button type="button" className="btn tiny" onClick={() => setDecided((x) => ({ ...x, [p.title]: 'no' }))}>Not this</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
